import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, TrendingUp, TrendingDown, PoundSterling, Home,
  AlertTriangle, CheckCircle, Users, Calendar, Loader2,
  BarChart3, PieChart, Activity, Clock, ArrowRight, XCircle
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, LineChart, Line, AreaChart, Area, Funnel, FunnelChart
} from "recharts";
import { API } from "@/App";

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
const STAGE_COLORS = {
  'Offer Accepted': '#3b82f6',
  'Memo Issued': '#8b5cf6',
  'AML Verified': '#6366f1',
  'Proof of Funds': '#a855f7',
  'Mortgage Offer': '#d946ef',
  'Contract Pack': '#ec4899',
  'Searches': '#f43f5e',
  'Enquiries': '#ef4444',
  'Exchanged': '#f59e0b',
  'Completion': '#10b981'
};

export const SalesAnalyticsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/sales/analytics`, { headers });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const stageData = analytics?.stages || [];
  const monthlyData = analytics?.monthly_trends || [];
  const statusData = analytics?.status_breakdown || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-700 to-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/sales")}
              className="text-white hover:bg-emerald-600"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Sales Analytics</h1>
              <p className="text-emerald-200">Pipeline performance & trends</p>
            </div>
          </div>
        </div>
      </header>

      {/* KPI Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Cases</p>
                  <p className="text-2xl font-bold">{analytics?.active_cases || 0}</p>
                </div>
                <Home className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                <Badge variant="secondary">{analytics?.exchanged_cases || 0} exchanged</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pipeline Value</p>
                  <p className="text-2xl font-bold">£{((analytics?.pipeline_value || 0) / 1000000).toFixed(1)}M</p>
                </div>
                <PoundSterling className="w-8 h-8 text-blue-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                <span className="text-muted-foreground">
                  Avg: £{((analytics?.avg_case_value || 0) / 1000).toFixed(0)}K
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed YTD</p>
                  <p className="text-2xl font-bold">{analytics?.completed_ytd || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                <span className="text-emerald-600">
                  £{((analytics?.completed_value || 0) / 1000000).toFixed(1)}M value
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Fall-Through Rate</p>
                  <p className="text-2xl font-bold">{analytics?.fall_through_rate || 0}%</p>
                </div>
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                {analytics?.fall_through_rate <= 15 ? (
                  <span className="text-emerald-600 flex items-center">
                    <TrendingDown className="w-4 h-4 mr-1" /> Below average
                  </span>
                ) : (
                  <span className="text-red-600 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1" /> Above average
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Monthly Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-600" />
                    Monthly Completions
                  </CardTitle>
                  <CardDescription>Cases completed by month</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Area 
                        type="monotone" 
                        dataKey="completed" 
                        stroke="#10b981" 
                        fill="#6ee7b7"
                        name="Completed"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="fallen_through" 
                        stroke="#ef4444" 
                        fill="#fca5a5"
                        name="Fallen Through"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Status Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-blue-600" />
                    Case Status Breakdown
                  </CardTitle>
                  <CardDescription>Current case distribution</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPie>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Pipeline Value by Month */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PoundSterling className="w-5 h-5 text-amber-600" />
                    Pipeline Value Trend
                  </CardTitle>
                  <CardDescription>Monthly pipeline value</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => `£${(value / 1000000).toFixed(1)}M`} />
                      <Tooltip formatter={(value) => `£${value.toLocaleString()}`} />
                      <Line 
                        type="monotone" 
                        dataKey="pipeline_value" 
                        stroke="#f59e0b" 
                        strokeWidth={2}
                        dot={{ fill: '#f59e0b' }}
                        name="Pipeline Value"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Average Days to Complete */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-600" />
                    Time to Completion
                  </CardTitle>
                  <CardDescription>Average days by stage</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics?.stage_durations || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" unit=" days" />
                      <YAxis dataKey="stage" type="category" width={100} fontSize={11} />
                      <Tooltip formatter={(value) => `${value} days`} />
                      <Bar dataKey="avg_days" fill="#8b5cf6" name="Avg Days" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pipeline Tab */}
          <TabsContent value="pipeline">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Pipeline Funnel */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-emerald-600" />
                    Sales Pipeline Funnel
                  </CardTitle>
                  <CardDescription>Cases by stage</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={stageData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="stage" angle={-45} textAnchor="end" height={80} fontSize={11} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" name="Cases">
                        {stageData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={STAGE_COLORS[entry.stage] || COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Pipeline Value by Stage */}
              <Card>
                <CardHeader>
                  <CardTitle>Pipeline Value by Stage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stageData.map((stage, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{stage.stage}</p>
                          <p className="text-sm text-muted-foreground">{stage.count} cases</p>
                        </div>
                        <Badge variant="secondary">
                          £{((stage.value || 0) / 1000).toFixed(0)}K
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Cases Needing Attention */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-amber-700">Cases Needing Attention</CardTitle>
                  <CardDescription>Stalled or at risk</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(analytics?.at_risk_cases || []).slice(0, 5).map((caseItem, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div>
                          <p className="font-medium">{caseItem.address}</p>
                          <p className="text-sm text-muted-foreground">{caseItem.stage}</p>
                        </div>
                        <Badge variant="outline" className="text-amber-700">
                          {caseItem.days_in_stage} days
                        </Badge>
                      </div>
                    ))}
                    {(!analytics?.at_risk_cases || analytics.at_risk_cases.length === 0) && (
                      <p className="text-center text-muted-foreground py-8">No cases at risk</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Conversion Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle>Conversion Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Offer to Exchange</p>
                        <p className="text-2xl font-bold">{analytics?.offer_to_exchange_rate || 0}%</p>
                      </div>
                      <ArrowRight className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Exchange to Completion</p>
                        <p className="text-2xl font-bold">{analytics?.exchange_to_completion_rate || 0}%</p>
                      </div>
                      <CheckCircle className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Avg Days to Exchange</p>
                        <p className="text-2xl font-bold">{analytics?.avg_days_to_exchange || 0}</p>
                      </div>
                      <Clock className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Avg Days to Completion</p>
                        <p className="text-2xl font-bold">{analytics?.avg_days_to_completion || 0}</p>
                      </div>
                      <Calendar className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Fall-Through Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-red-700">Fall-Through Analysis</CardTitle>
                  <CardDescription>Common reasons for fallen deals</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPie>
                      <Pie
                        data={analytics?.fall_through_reasons || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {(analytics?.fall_through_reasons || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Top Performers */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Year-to-Date Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <p className="text-3xl font-bold text-emerald-700">{analytics?.completed_ytd || 0}</p>
                      <p className="text-sm text-muted-foreground">Completed</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-3xl font-bold text-blue-700">{analytics?.exchanged_ytd || 0}</p>
                      <p className="text-sm text-muted-foreground">Exchanged</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="text-3xl font-bold text-amber-700">{analytics?.new_instructions_ytd || 0}</p>
                      <p className="text-sm text-muted-foreground">New Instructions</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                      <p className="text-3xl font-bold text-red-700">{analytics?.fallen_through_ytd || 0}</p>
                      <p className="text-sm text-muted-foreground">Fallen Through</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SalesAnalyticsPage;
